// El estado del plan de una sesión y TODAS sus acciones (ADR-2026-10-13-catalogo-con-el-agente).
// Son las mismas para la pantalla, el panel del ítem y el agente (sus herramientas llaman acá:
// nunca un setState que saltee estas reglas). Cada cambio pasa por `setPlan` y `usePlanGuardado`
// lo persiste en `lienzo`. Las medidas y la ficha se guardan en el producto (Mi marca) con
// debounce, además de quedar en el snapshot del plan.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FichaDeProducto } from '../../../../lib/fichaDeProducto';
import type { Medidas } from '../../../../lib/medidas';
import type { ResolucionImagen } from '../../../../lib/resolucionImagen';
import { saveFichaDeVariante, saveMedidasDeVariante } from '../../medidasApi';
import { actualizarPlanEnRef, type CambioDePlan } from './actualizarPlanEnRef';
import { escribirPrompts, type ResultadoDeEscritura } from './escritorDeCatalogo';
import { fundirEstados, fundirPrompts } from './fundirPlan';
import { GuardadoEnProducto, type GuardarEnProducto } from './guardadoEnProducto';
import { generarCatalogo, reanudarLotes, type ResultadoDeGeneracion, type TandaApi } from './generarCatalogo';
import {
  invalidarInfografiasDelProducto,
  invalidarPrompts,
  invalidarToma,
  normalizarEtiquetas,
  itemsPendientesDeEscritura,
  marcarEdicion,
  marcarRehacer,
  ponerReferencia,
  quitarPendientes,
  sacarProductoDeToma,
  sincronizarItems,
  sumarProductos as sumarProductosAlPlan,
  type FiltroDeItems,
  type PlanDeCatalogo,
  type ProductoParaElPlan,
  type ProductosSumados,
  type ReferenciaDelPlan,
  type TomaDelPlan,
} from './plan';
import { leerPlan, usePlanGuardado } from './planGuardado';

export interface AccionesDelPlan {
  setTitulo: (t: string) => void;
  /** Con `reescribir`, lo escrito y no fijado vuelve a "sin prompt": el estilo manda sobre todos los prompts. */
  setEstilo: (e: string, opts?: { reescribir?: boolean }) => void;
  setFormato: (slug: string) => void;
  setResolucion: (r: ResolucionImagen) => void;
  /** Reemplaza las tomas (resincroniza los ítems, conservando los que siguen). */
  reemplazarTomas: (tomas: TomaDelPlan[]) => void;
  agregarToma: (toma: TomaDelPlan) => void;
  quitarToma: (id: string) => void;
  editarToma: (id: string, parcial: Partial<Omit<TomaDelPlan, 'id'>>) => void;
  quitarProducto: (varianteId: string) => void;
  /** Suma productos al final (con las tomas de `tomaIds`, o todas): ADR-2026-10-15-el-agente-suma-productos-al-catalogo. */
  sumarProductos: (productos: ProductoParaElPlan[], tomaIds?: string[]) => ProductosSumados;
  /** Saca una toma de UN producto (su ítem se va; si era el último, la toma entera). */
  quitarItem: (varianteId: string, tomaId: string) => void;
  setNotas: (varianteId: string, notas: string) => void;
  /** Las etiquetas de la ficha de medidas de un producto ([] = volver a las de la ficha). Sus
   *  fichas escritas y sin foto vuelven a "sin prompt" (ADR-2026-10-19-las-etiquetas-de-la-ficha-son-las-que-importan). */
  setEtiquetas: (varianteId: string, etiquetas: string[]) => void;
  /** Suma una imagen como referencia o le cambia la etiqueta (ADR-2026-10-15-la-plantilla-de-marca). */
  ponerReferencia: (ref: ReferenciaDelPlan) => void;
  setMedidas: (varianteId: string, medidas: Medidas) => void;
  setFicha: (varianteId: string, ficha: FichaDeProducto) => void;
  /** Deja el prompt y lo FIJA: el escritor no lo pisa. */
  editarPrompt: (itemId: string, prompt: string) => void;
  desfijar: (itemId: string) => void;
  /** Escribe con el escritor del backend los ítems pendientes (o los de `solo`), con una
   *  indicación puntual para esta reescritura si la hay. */
  escribir: (solo?: FiltroDeItems, indicacion?: string) => Promise<ResultadoDeEscritura>;
  /** Deja una edición pendiente en las fotos ya salidas (ADR-2026-10-14-el-agente-edita-el-catalogo-en-masa). */
  marcarEdicion: (solo: FiltroDeItems | undefined, instruccion: string) => { marcados: string[]; sinFoto: number };
  /** Deja un rehacer pendiente en las fotos ya salidas. */
  marcarRehacer: (solo: FiltroDeItems | undefined) => { marcados: string[] };
  /** Saca las ediciones y los rehacer pendientes (sin filtro, todos). */
  quitarPendientes: (solo?: FiltroDeItems) => void;
  /** Genera todo (o `soloItems`) en lotes en serie. */
  generar: (soloItems?: string[]) => Promise<ResultadoDeGeneracion>;
  /** Registra que el agente pidió generar: espera al click en Generar (o a "No generar"). Si ya hay un pedido esperando, responde que no. */
  pedirGenerar: () => Promise<ResultadoDeGeneracion>;
  /** La persona no quiso generar: resuelve el pedido pendiente con `ok: false`. */
  cancelarGenerar: () => void;
  abrirItem: (id: string | null) => void;
  /** Vuelve a guardar en el producto los datos que no se pudieron guardar. */
  reintentarGuardado: () => void;
}

export interface PlanDeSesion {
  plan: PlanDeCatalogo | null;
  setPlan: (p: CambioDePlan) => void;
  cargando: boolean;
  noExiste: boolean;
  escribiendo: boolean;
  generando: boolean;
  /** El agente pidió generar y espera el click. */
  generarPedido: boolean;
  itemAbierto: string | null;
  /** Productos (varianteId) cuyos datos no se pudieron guardar en Mi marca, ni reintentando: siguen
   *  en el plan, pero un catálogo nuevo con ese producto no los tendría. */
  productosSinGuardar: string[];
  acciones: AccionesDelPlan;
  /** El plan al instante, con lo que las acciones de este mismo tick ya cambiaron (`plan`
   *  llega recién en el render siguiente). Es lo que leen las herramientas del agente. */
  leerPlan: () => PlanDeCatalogo | null;
}


export function usePlanDeSesion(p: {
  chatId: string | undefined;
  companyId: string | null;
  tanda: TandaApi;
  /** El plan que la portada acaba de guardar: se dibuja en el primer frame (la view transition
   *  de la portada a la sesión no puede esperar a leerlo). */
  planInicial?: PlanDeCatalogo | null;
}): PlanDeSesion {
  const [plan, setPlanState] = useState<PlanDeCatalogo | null>(p.planInicial ?? null);
  const [cargando, setCargando] = useState(!p.planInicial);
  const [noExiste, setNoExiste] = useState(false);
  const [escribiendo, setEscribiendo] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [generarPedido, setGenerarPedido] = useState(false);
  const [itemAbierto, setItemAbierto] = useState<string | null>(null);
  const planRef = useRef<PlanDeCatalogo | null>(p.planInicial ?? null);
  planRef.current = plan;
  const resolverGenerar = useRef<((r: ResultadoDeGeneracion) => void) | null>(null);
  const vivo = useRef(true);
  // Al salir de la pantalla, la generación en curso no arranca lotes nuevos: sus `execution_id`
  // ya no se guardarían en el plan y al volver Generar los lanzaría de nuevo (review 2026-10-15).
  const salida = useRef(new AbortController());

  // El ref es la fuente de verdad y se actualiza SINCRÓNICAMENTE: las herramientas del agente
  // encadenan acciones y leen el plan entre medio (review 2026-10-14, punto 1).
  const setPlan = useCallback((v: CambioDePlan) => actualizarPlanEnRef(planRef, setPlanState, v), []);

  usePlanGuardado(p.chatId, plan);

  // ── Cargar y reanudar ──────────────────────────────────────────────────────
  const { chatId, companyId, tanda } = p;
  const traiaPlan = useRef(!!p.planInicial);
  useEffect(() => {
    vivo.current = true;
    if (salida.current.signal.aborted) salida.current = new AbortController();
    const alSalir = () => {
      vivo.current = false;
      salida.current.abort();
    };
    if (!chatId) return alSalir;
    // Recién creado por la portada: ya está en pantalla y no tiene lotes que reanudar.
    if (traiaPlan.current) {
      traiaPlan.current = false;
      return alSalir;
    }
    setCargando(true);
    setNoExiste(false);
    void leerPlan(chatId).then(async (leido) => {
      if (!vivo.current) return;
      if (!leido) {
        setNoExiste(true);
        setCargando(false);
        return;
      }
      setPlan(leido);
      setCargando(false);
      if (companyId) {
        // Lo que llega se funde sobre el plan vivo: la persona ya puede tocarlo mientras se reanuda.
        await reanudarLotes({ plan: leido, companyId, chatId, tanda, onPlan: (pl) => vivo.current && setPlan((prev) => fundirEstados(prev, pl)) });
      }
    });
    return alSalir;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // ── Guardado de medidas y ficha en el producto, con debounce por variante ─
  const [productosSinGuardar, setProductosSinGuardar] = useState<string[]>([]);
  const guardado = useRef<GuardadoEnProducto | null>(null);
  if (!guardado.current) guardado.current = new GuardadoEnProducto((ids) => vivo.current && setProductosSinGuardar(ids));
  const guardarEnProducto = useCallback((clave: string, varianteId: string, fn: GuardarEnProducto) => {
    void guardado.current!.guardar(clave, varianteId, fn);
  }, []);

  const acciones = useMemo<AccionesDelPlan>(() => {
    const conTomas = (f: (tomas: TomaDelPlan[]) => TomaDelPlan[]) =>
      setPlan((prev) => sincronizarItems({ ...prev, tomas: f(prev.tomas) }));
    const conItem = (itemId: string, f: (i: PlanDeCatalogo['productos'][number]['items'][number]) => typeof i) =>
      setPlan((prev) => ({
        ...prev,
        productos: prev.productos.map((pr) => ({ ...pr, items: pr.items.map((i) => (i.id === itemId ? f(i) : i)) })),
      }));
    const conProducto = (varianteId: string, f: (pr: PlanDeCatalogo['productos'][number]) => typeof pr) =>
      setPlan((prev) => ({ ...prev, productos: prev.productos.map((pr) => (pr.varianteId === varianteId ? f(pr) : pr)) }));

    return {
      setTitulo: (titulo) => setPlan((prev) => ({ ...prev, titulo: titulo.trim().slice(0, 80) || prev.titulo })),
      setEstilo: (estilo, opts) =>
        setPlan((prev) => {
          const conEstilo = { ...prev, estilo: estilo.slice(0, 1500) };
          return opts?.reescribir && prev.estilo.trim() !== estilo.trim() ? invalidarPrompts(conEstilo) : conEstilo;
        }),
      setFormato: (formato) => setPlan((prev) => ({ ...prev, formato })),
      setResolucion: (resolucion) => setPlan((prev) => ({ ...prev, resolucion })),
      reemplazarTomas: (tomas) => conTomas(() => tomas),
      agregarToma: (toma) => conTomas((tomas) => (tomas.some((t) => t.id === toma.id) ? tomas : [...tomas, toma])),
      quitarToma: (id) => conTomas((tomas) => tomas.filter((t) => t.id !== id)),
      editarToma: (id, parcial) =>
        setPlan((prev) => {
          const tomas = prev.tomas.map((t) => (t.id === id ? { ...t, ...parcial, id } : t));
          const cambiaLoEscrito = 'queMuestra' in parcial || 'fondoBlanco' in parcial || 'luzFondoBlanco' in parcial || 'tarea' in parcial;
          const plan2 = sincronizarItems({ ...prev, tomas });
          const cambiaLaInfografia = 'infografia' in parcial || 'pillsDeLaFicha' in parcial || 'colorInfografia' in parcial || 'estiloGrafico' in parcial;
          if (!cambiaLoEscrito && !cambiaLaInfografia) return plan2;
          // Lo que la toma dice cambió: sus prompts escritos (sin foto, no fijados) vuelven a "sin prompt".
          return invalidarToma(plan2, id);
        }),
      quitarProducto: (varianteId) => setPlan((prev) => ({ ...prev, productos: prev.productos.filter((pr) => pr.varianteId !== varianteId) })),
      sumarProductos: (productos, tomaIds) => {
        const actual = planRef.current;
        if (!actual) return { plan: actual as unknown as PlanDeCatalogo, sumados: [], yaEstaban: [], sinLugar: productos.map((p) => p.varianteId) };
        const r = sumarProductosAlPlan(actual, productos, tomaIds);
        if (r.sumados.length > 0) setPlan(r.plan);
        return r;
      },
      quitarItem: (varianteId, tomaId) => setPlan((prev) => sacarProductoDeToma(prev, varianteId, tomaId)),
      setNotas: (varianteId, notas) => conProducto(varianteId, (pr) => ({ ...pr, notas: notas.slice(0, 600) })),
      setEtiquetas: (varianteId, etiquetas) =>
        setPlan((prev) => {
          const limpias = normalizarEtiquetas(etiquetas);
          const productos = prev.productos.map((pr) => {
            if (pr.varianteId !== varianteId) return pr;
            const { etiquetas: _viejas, ...resto } = pr;
            return limpias.length ? { ...resto, etiquetas: limpias } : resto;
          });
          return invalidarInfografiasDelProducto({ ...prev, productos }, varianteId);
        }),
      ponerReferencia: (ref) => setPlan((prev) => ponerReferencia(prev, ref)),
      setMedidas: (varianteId, medidas) => {
        conProducto(varianteId, (pr) => ({ ...pr, medidas }));
        guardarEnProducto(`m:${varianteId}`, varianteId, () => saveMedidasDeVariante(varianteId, medidas));
      },
      setFicha: (varianteId, ficha) => {
        conProducto(varianteId, (pr) => ({ ...pr, ficha }));
        guardarEnProducto(`f:${varianteId}`, varianteId, () => saveFichaDeVariante(varianteId, ficha));
      },
      // Lo pendiente (una edición, un rehacer) era sobre la foto del prompt viejo: se va. La foto
      // sigue en la tarjeta y Generar la rehace con el nuevo (ADR-2026-10-15-el-agente-ve-y-cambia-las-tomas).
      editarPrompt: (itemId, prompt) =>
        conItem(itemId, (i) => {
          const { edicion: _e, rehacer: _r, ...resto } = i;
          return { ...resto, prompt, fijado: true, estado: prompt.trim() ? 'escrito' : 'sin-prompt', motivo: undefined };
        }),
      desfijar: (itemId) => conItem(itemId, (i) => ({ ...i, fijado: false })),
      escribir: async (solo, indicacion) => {
        const actual = planRef.current;
        if (!actual) return { plan: actual as unknown as PlanDeCatalogo, escritos: 0, fallidos: [] };
        const items = itemsPendientesDeEscritura(actual, solo);
        if (items.length === 0) return { plan: actual, escritos: 0, fallidos: [] };
        setEscribiendo(true);
        try {
          const r = await escribirPrompts({
            plan: actual,
            items,
            indicacion,
            onProducto: (pl) => {
              if (!vivo.current) return;
              // Lo que llega del escritor se funde sobre lo que la persona pudo tocar mientras tanto.
              setPlan((prev) => fundirPrompts(prev, pl));
            },
          });
          return { ...r, plan: planRef.current ?? r.plan };
        } finally {
          if (vivo.current) setEscribiendo(false);
        }
      },
      marcarEdicion: (solo, instruccion) => {
        let r = { marcados: [] as string[], sinFoto: 0 };
        setPlan((prev) => {
          const m = marcarEdicion(prev, solo, instruccion);
          r = { marcados: m.marcados, sinFoto: m.sinFoto };
          return m.plan;
        });
        return r;
      },
      marcarRehacer: (solo) => {
        let r = { marcados: [] as string[] };
        setPlan((prev) => {
          const m = marcarRehacer(prev, solo);
          r = { marcados: m.marcados };
          return m.plan;
        });
        return r;
      },
      quitarPendientes: (solo) => setPlan((prev) => quitarPendientes(prev, solo)),
      generar: async (soloItems) => {
        const actual = planRef.current;
        if (!actual || !companyId || !chatId) {
          return { ok: false, error: 'La sesión no está lista.', sinCreditos: false, plan: actual as unknown as PlanDeCatalogo, lanzados: 0 };
        }
        setGenerando(true);
        setGenerarPedido(false);
        try {
          const r = await generarCatalogo({
            plan: actual,
            companyId,
            chatId,
            tanda,
            soloItems,
            noLanzarMas: salida.current.signal,
            onPlan: (pl) => vivo.current && setPlan((prev) => fundirEstados(prev, pl)),
          });
          resolverGenerar.current?.(r);
          resolverGenerar.current = null;
          return r;
        } finally {
          if (vivo.current) setGenerando(false);
        }
      },
      pedirGenerar: () => {
        if (resolverGenerar.current) {
          return Promise.resolve({ ok: false as const, error: 'ya hay un pedido de generar esperando el click', sinCreditos: false, plan: planRef.current!, lanzados: 0 });
        }
        return new Promise<ResultadoDeGeneracion>((resolve) => {
          resolverGenerar.current = resolve;
          setGenerarPedido(true);
        });
      },
      cancelarGenerar: () => {
        const r = resolverGenerar.current;
        resolverGenerar.current = null;
        setGenerarPedido(false);
        r?.({ ok: false, error: 'la persona no quiso generar todavía', sinCreditos: false, plan: planRef.current!, lanzados: 0 });
      },
      abrirItem: setItemAbierto,
      reintentarGuardado: () => guardado.current!.reintentar(),
    };
  }, [setPlan, guardarEnProducto, companyId, chatId, tanda]);

  const leerPlanVivo = useCallback(() => planRef.current, []);
  return { plan, setPlan, cargando, noExiste, escribiendo, generando, generarPedido, itemAbierto, productosSinGuardar, acciones, leerPlan: leerPlanVivo };
}
